// The planner page furniture, shared by every Today.
//
// Five dashboards, one per role, all answering a different question about the
// same morning. They should not each invent a heading and a section label —
// that is how five pages end up looking like five products.
//
// The idiom is Franklin / Day-Timer, which the availability calendar already
// reads in: pale green ground, hunter ink, hairline rules, and every section
// wearing a small capital label. The classes live in styles/index.css; this
// file is the shapes they make.

import { Link } from 'react-router-dom';
import { fmtDate, todayLong } from '../../lib/dates';

/**
 * The top of a planner page: the day's numeral, big, then the weekday, then the
 * date under it.
 *
 * The weekday is the heading rather than the word "Today", because the page can
 * be flipped. "Today" is only true on one of them, and a heading that lies on
 * every other day is worse than one that simply says which day you are on —
 * which is what a planner page says, in the same place, every time.
 */
export function DayHeader({ today, date, title, action, onToday }) {
  const shown = date || today;
  const isToday = !shown || !today || shown === today;
  return (
    <header className="flex items-end justify-between gap-3 mb-4 pb-2 border-b-2 border-hunter/30">
      <div className="flex items-end gap-3 min-w-0">
        <span className="text-4xl md:text-5xl font-semibold leading-none text-hunter tabular-nums">
          {shown ? Number(shown.slice(8, 10)) : '—'}
        </span>
        <span className="pb-0.5 min-w-0">
          <h1 className="text-lg md:text-xl font-semibold leading-tight flex items-baseline gap-2">
            {/* Monday. Thursday. The day you turned to. */}
            {title ?? (shown ? fmtDate(shown, 'weekdayLong') : 'Today')}
            {!isToday && (
              <button
                type="button"
                onClick={onToday}
                className="text-xs font-normal text-hunter hover:underline"
              >
                back to today
              </button>
            )}
          </h1>
          <time className="text-sm text-ink-500 block truncate">
            {shown ? fmtDate(shown, 'monthDay') : todayLong()}
          </time>
        </span>
      </div>
      {action}
    </header>
  );
}

/**
 * One section: a small capital label, an optional count or link, ruled rows.
 *
 * `count` is deliberately hidden when zero. "Overdue 0" is a number somebody
 * has to read and then discard; an empty section says its own emptiness in
 * words underneath.
 */
export function Panel({ title, count, action, children }) {
  return (
    <section className="planner">
      <header className="planner-band">
        <span>{title}</span>
        {action ?? (count ? <span className="tabular-nums font-normal">{count}</span> : null)}
      </header>
      {children}
    </section>
  );
}

/** Nothing here — said quietly, with a way out if there is one. */
export function Empty({ children }) {
  return <p className="planner-empty">{children}</p>;
}

/**
 * A row that goes somewhere.
 *
 * `to` is required and that is the point of this component existing: the old
 * dashboards made the whole CARD a link to a generic index, so clicking a
 * horse's name took you to the list of every horse. A row that names a record
 * should open that record.
 */
export function Row({ to, children, className = '' }) {
  return (
    <li>
      <Link to={to} className={'planner-row ' + className}>
        {children}
      </Link>
    </li>
  );
}

/** The ruled list a Panel's rows sit in. */
export function Rows({ children }) {
  return <ul className="planner-rows">{children}</ul>;
}

/** Two columns on a wide screen, stacked on a phone: month and lists, then the day. */
export function PlannerGrid({ aside, children }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[20rem_1fr] gap-4 items-start">
      <div className="space-y-4">{aside}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

export function PlannerSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[20rem_1fr] gap-4">
      <div className="h-64 planner animate-pulse" />
      <div className="h-64 planner animate-pulse" />
    </div>
  );
}
